"use client"

/**
 * Kéo-thả để đổi độ rộng cột cho antd Table (không cần thêm thư viện).
 *
 * Dùng:
 *   const { applyWidths, components } = useResizableColumns("post")   // trước mọi early-return
 *   const columns = applyWidths(displayColumns)
 *   <Table columns={columns} components={components} scroll={{ x: columnScrollX(columns) }} />
 *
 * Độ rộng đã kéo được lưu localStorage theo từng module (per-user, per-browser),
 * cùng tinh thần với view-config. Cột không có `width` dạng số (vd. cột chọn) bỏ qua.
 */

import React, { useCallback, useEffect, useRef, useState } from "react"

const MIN_COLUMN_WIDTH = 80
const MAX_COLUMN_WIDTH = 1200

const storageKey = (moduleKey: string) => `admin:column-widths:${moduleKey}`

type Widths = Record<string, number>

type ResizableHeaderCellProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  resizeWidth?: number
  onResizeWidth?: (width: number) => void
  onResizeEnd?: () => void
}

/** `<th>` có tay nắm ở mép phải; kéo để đổi độ rộng cột. */
function ResizableHeaderCell({ resizeWidth, onResizeWidth, onResizeEnd, ...rest }: ResizableHeaderCellProps) {
  const startRef = useRef<{ x: number; width: number } | null>(null)

  if (!resizeWidth || !onResizeWidth) return <th {...rest} />

  const onPointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault()
    e.stopPropagation()
    startRef.current = { x: e.clientX, width: resizeWidth }
    e.currentTarget.setPointerCapture(e.pointerId)
    document.body.classList.add("admin-col-resizing")
  }

  const onPointerMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!startRef.current) return
    const next = startRef.current.width + (e.clientX - startRef.current.x)
    onResizeWidth(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(next))))
  }

  const onPointerUp = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!startRef.current) return
    startRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    document.body.classList.remove("admin-col-resizing")
    onResizeEnd?.()
  }

  return (
    <th {...rest} style={{ ...rest.style, position: "relative" }}>
      {rest.children}
      <span
        role="separator"
        aria-orientation="vertical"
        aria-label="Kéo để đổi độ rộng cột"
        className="admin-col-resize-handle"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        // Không để click trên tay nắm kích hoạt sort của cột.
        onClick={e => e.stopPropagation()}
      />
    </th>
  )
}

const tableComponents = { header: { cell: ResizableHeaderCell } }

type ResizableColumn = {
  key?: React.Key
  width?: unknown
  onHeaderCell?: (...args: any[]) => any
}

export function useResizableColumns(moduleKey: string) {
  const [widths, setWidths] = useState<Widths>({})
  const widthsRef = useRef<Widths>({})

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey(moduleKey))
      if (raw) {
        const parsed = JSON.parse(raw) as Widths
        widthsRef.current = parsed
        setWidths(parsed)
      }
    } catch {
      /* localStorage không khả dụng — dùng độ rộng mặc định. */
    }
  }, [moduleKey])

  const setWidth = useCallback((key: string, width: number) => {
    widthsRef.current = { ...widthsRef.current, [key]: width }
    setWidths(widthsRef.current)
  }, [])

  const persist = useCallback(() => {
    try {
      window.localStorage.setItem(storageKey(moduleKey), JSON.stringify(widthsRef.current))
    } catch {
      /* quota / private mode — giữ trong bộ nhớ. */
    }
  }, [moduleKey])

  const resetWidths = useCallback(() => {
    widthsRef.current = {}
    setWidths({})
    try {
      window.localStorage.removeItem(storageKey(moduleKey))
    } catch {
      /* ignore */
    }
  }, [moduleKey])

  /** Gán độ rộng đã lưu + tay nắm kéo cho các cột có `width` dạng số. Là hàm
   *  thường (không phải hook) để gọi được sau các early-return của trang. */
  const applyWidths = useCallback(
    <C extends ResizableColumn>(columns: readonly C[]): C[] =>
      columns.map(col => {
        const key = col.key != null ? String(col.key) : undefined
        if (!key || typeof col.width !== "number") return col
        const width = widths[key] ?? col.width
        const originalOnHeaderCell = col.onHeaderCell
        return {
          ...col,
          width,
          onHeaderCell: (column: unknown) => ({
            ...(originalOnHeaderCell?.(column) ?? {}),
            resizeWidth: width,
            onResizeWidth: (next: number) => setWidth(key, next),
            onResizeEnd: persist,
          }),
        }
      }),
    [widths, setWidth, persist],
  )

  return { applyWidths, components: tableComponents, resetWidths }
}
